import { TZDateMini } from '@date-fns/tz';
import { isAfter, isBefore } from 'date-fns';
import { type Dispatch, type RefObject } from 'react';
import {
  FormattedMessage,
  defineMessages,
  type IntlShape,
  type MessageDescriptor,
} from 'react-intl';

import { parseTimeAsTimeValue } from '@dynatrace-sdk/units';
import { getTimezone } from '@dynatrace-sdk/user-preferences';

import {
  isFullTimeframe,
  isPartialTimeframe,
  isTimeframe,
  isTimeValue,
  getFormattedMinMax,
} from './helpers.js';
import type { Precision, TimeErrorKey } from './shared.types.js';
import {
  type FormFieldMessagesReducerAction,
  FormFieldMessagesAction,
} from '../../forms/forms-core/state/formfield-messages-state-reducer.js';
import {
  GRAIL_MAX_DATE,
  GRAIL_MIN_DATE,
} from '../../forms/forms-core/time/date-time.js';
import { ValidityStateKey } from '../../forms/validation/types.js';
import { type Timeframe, type TimeValue } from '../types/time.js';

type TimeBoundaryErrorKey = Extract<
  TimeErrorKey,
  'maxBoundary' | 'minBoundary' | 'minMaxBoundary'
>;

type TimeBoundaryMessageValues = {
  min: string;
  max: string;
  term: string;
};

type TimeBoundaryMessageData = {
  errorKey: TimeBoundaryErrorKey;
  values: TimeBoundaryMessageValues;
};

/** @internal */
export type ValidateOptions = {
  formMessageDispatch?: Dispatch<FormFieldMessagesReducerAction>;
  formerError?: TimeErrorKey;
  precision?: Precision;
  showError?: boolean;
  checkOrder?: boolean;
  ref?: RefObject<unknown>;
  expectedFormat?: string;
  valueMode?: 'single' | 'timeframeSelector' | 'dateTimePicker';
  required?: boolean;
  /** Optional per-key message overrides. Falls back to built-in defaults for any omitted keys. @internal */
  errorMessages?: Partial<Record<TimeErrorKey, MessageDescriptor>>;
};

/**
 * Checks if a date is outside the given min and/or max bounds.
 * @internal
 */
export function isOutOfBounds(
  date?: string | Date,
  min?: TimeValue | null,
  max?: TimeValue | null,
): TimeErrorKey | undefined {
  // All parse times here need to use the same _now_ date, to not be subject
  // to millisecond differences during parsing.
  const relativeNow = TZDateMini.tz(getTimezone()).getTime();

  const newDate =
    typeof date === 'string'
      ? parseTimeAsTimeValue(date, relativeNow)?.absoluteDate
      : date;

  if (!newDate) {
    return undefined;
  }

  // Re-evaluate relative expressions using the same "now" as the value so
  // that min/max stay temporally consistent with the date being checked.
  // Using a stale absoluteDate from a midnight refresh would cause spurious
  // out-of-bounds errors when the value is evaluated at the current time.
  const minDate =
    min?.type === 'expression'
      ? parseTimeAsTimeValue(min.value, relativeNow)?.absoluteDate
      : min?.absoluteDate;
  const maxDate =
    max?.type === 'expression'
      ? parseTimeAsTimeValue(max.value, relativeNow)?.absoluteDate
      : max?.absoluteDate;

  if (
    minDate &&
    maxDate &&
    (isBefore(newDate, minDate) || isAfter(newDate, maxDate))
  ) {
    return 'minMaxBoundary';
  }

  if (minDate && !maxDate && isBefore(newDate, minDate)) {
    return 'minBoundary';
  }

  if (!minDate && maxDate && isAfter(newDate, maxDate)) {
    return 'maxBoundary';
  }

  return undefined;
}
const DEFAULT_ERROR_MESSAGES = defineMessages({
  minBoundary: {
    id: 'KETnWaCwo9qLRQrI',
    defaultMessage: 'Only dates after {min} are available.',
    description: 'Given dates are not within the valid range.',
  },
  maxBoundary: {
    id: 'ONYnUtFidz4+6NmE',
    defaultMessage: 'Only dates before {max} are available.',
    description: 'Given dates are not within the valid range.',
  },
  minMaxBoundary: {
    id: 'Qm0/7EQKVXjS9tDM',
    defaultMessage: 'Only dates between {min} → {max} are available.',
    description: 'Given dates are not within the valid range.',
  },
  invalidOrder: {
    defaultMessage: "'from' date should be before 'to' date",
    id: 'DKFIWE64kHV1ohuk',
    description:
      'Message to display if the from and to dates are in the wrong order.',
  },
  singleValueMissing: {
    defaultMessage: 'Please fill in this field.',
    id: 'hIK5810KwQytwP1q',
    description: 'DateTimePicker required value is missing.',
  },
  missingFrom: {
    defaultMessage: 'Please enter a start time.',
    id: 'GUeetpTpusqFkMi+',
    description: 'From value is missing.',
  },
  missingTo: {
    defaultMessage: 'Please enter an end time.',
    id: 'tfHTZkRc7sr/Ypcl',
    description: 'End value is missing.',
  },
  missingFromTo: {
    defaultMessage: 'Please enter a start and end time.',
    id: '4yANH5gdmW1efk0I',
    description: 'Start and end values are missing.',
  },
  valueMissing: {
    defaultMessage: 'Empty timeframe.',
    id: 'QsKlSIxE+Yp58CUG',
    description: 'Timeframe selector required timeframe error hint.',
  },
  invalid: {
    defaultMessage:
      'Invalid time. Expected format: {expectedFormat}, -2h or -2d@d',
    id: 'r/XpT4mat98jCrup',
    description: 'Given value is invalid',
  },
  invalidFrom: {
    defaultMessage:
      'Invalid start time. Expected format: {expectedFormat}, -2h or -2d@d',
    id: 'supuOfI2cSrNKOZ/',
    description: 'Given from value is invalid',
  },
  invalidTo: {
    defaultMessage:
      'Invalid end time. Expected format: {expectedFormat}, -2h or -2d@d',
    id: 'HL9dGWGnhYkm7OyC',
    description: 'Given to value is invalid',
  },
  invalidFromTo: {
    defaultMessage:
      'Invalid start and end time. Expected format: {expectedFormat}, -2h or -2d@d',
    id: '89sBAyzLbeW6GyPJ',
    description: 'Given from and to values are invalid',
  },
});

/**
 * Checks whether any part of the given value falls outside the default Grail date range.
 */
function isValueOutsideGrailRange(
  value: { from: TimeValue | null; to: TimeValue | null } | TimeValue | null,
): boolean {
  if (!value) {
    return false;
  }

  const grailMinDate = new Date(GRAIL_MIN_DATE);
  const grailMaxDate = new Date(GRAIL_MAX_DATE);

  if (isTimeValue(value)) {
    if (!value.absoluteDate) {
      return false;
    }
    const date = new Date(value.absoluteDate);
    return isBefore(date, grailMinDate) || isAfter(date, grailMaxDate);
  }

  const fromDate = value.from?.absoluteDate
    ? new Date(value.from.absoluteDate)
    : null;
  const toDate = value.to?.absoluteDate
    ? new Date(value.to.absoluteDate)
    : null;

  return (
    (fromDate !== null &&
      (isBefore(fromDate, grailMinDate) || isAfter(fromDate, grailMaxDate))) ||
    (toDate !== null &&
      (isBefore(toDate, grailMinDate) || isAfter(toDate, grailMaxDate)))
  );
}

/**
 * Filters a GRAIL default min or max TimeValue.
 * Returns `undefined` when the limit is a default Grail boundary and the value
 * is within the Grail range (so the Grail limit should not appear in messages).
 * Custom (non-Grail) limits are always kept.
 */
function filterGrailTimeValue(
  value: { from: TimeValue | null; to: TimeValue | null } | TimeValue | null,
  limit?: TimeValue | null,
): TimeValue | null | undefined {
  if (!limit) {
    return limit;
  }

  const isGrailLimit =
    limit.value === GRAIL_MIN_DATE || limit.value === GRAIL_MAX_DATE;

  if (!isGrailLimit) {
    return limit;
  }

  return isValueOutsideGrailRange(value) ? limit : null;
}

function isTimeBoundaryErrorKey(
  errorKey?: TimeErrorKey,
): errorKey is TimeBoundaryErrorKey {
  return (
    errorKey === 'minBoundary' ||
    errorKey === 'maxBoundary' ||
    errorKey === 'minMaxBoundary'
  );
}

function parseLimit(
  limit?: TimeValue | string | null,
  relativeNow: number = TZDateMini.tz(getTimezone()).getTime(),
): TimeValue | null | undefined {
  if (typeof limit !== 'string') {
    return limit;
  }

  return parseTimeAsTimeValue(limit, relativeNow) ?? null;
}

function getBoundaryErrorKey(
  min?: TimeValue | null,
  max?: TimeValue | null,
): TimeBoundaryErrorKey | undefined {
  if (min && max) {
    return 'minMaxBoundary';
  }

  if (min) {
    return 'minBoundary';
  }

  if (max) {
    return 'maxBoundary';
  }

  return undefined;
}

/**
 * Gets the timeframe boundary message data while hiding default Grail limits
 * unless the current value actually exceeds the Grail range.
 * @internal
 */
export function getTimeBoundaryMessageData(
  value: { from: TimeValue | null; to: TimeValue | null } | TimeValue | null,
  min?: TimeValue | string | null,
  max?: TimeValue | string | null,
  precision: Precision = 'minutes',
  onlyWhenOutOfBounds: boolean = true,
  term?: string,
): TimeBoundaryMessageData | undefined {
  const relativeNow = TZDateMini.tz(getTimezone()).getTime();
  const parsedMin = parseLimit(min, relativeNow);
  const parsedMax = parseLimit(max, relativeNow);
  const filteredMin = filterGrailTimeValue(value, parsedMin);
  const filteredMax = filterGrailTimeValue(value, parsedMax);

  const errorKey = onlyWhenOutOfBounds
    ? getOutOfBoundsErrorKey(value, filteredMin, filteredMax)
    : getBoundaryErrorKey(filteredMin, filteredMax);

  if (!isTimeBoundaryErrorKey(errorKey)) {
    return undefined;
  }

  const { min: formattedMin, max: formattedMax } = getFormattedMinMax(
    filteredMin?.value,
    filteredMax?.value,
    precision,
  );

  const resolvedTerm =
    term ??
    (value && ('from' in value || 'to' in value) ? 'timeframe' : 'date');

  return {
    errorKey,
    values: {
      min: formattedMin,
      max: formattedMax,
      term: resolvedTerm,
    },
  };
}

/** validates a Timeframe or a TimeValue against their min and max borders */
function getOutOfBoundsErrorKey(
  updatedValue:
    | { from: TimeValue | null; to: TimeValue | null }
    | TimeValue
    | null,
  min?: TimeValue | null,
  max?: TimeValue | null,
): TimeErrorKey | undefined {
  // Range value
  if (
    updatedValue &&
    ('from' in updatedValue || 'to' in updatedValue) &&
    isTimeframe(updatedValue, 'range')
  ) {
    const fromErrorKey = isOutOfBounds(updatedValue?.from?.value, min, max);
    const toErrorKey = isOutOfBounds(updatedValue?.to?.value, min, max);
    return fromErrorKey || toErrorKey;
  }

  // Single value
  if (updatedValue && isTimeValue(updatedValue)) {
    return isOutOfBounds(updatedValue.value, min, max);
  }
}

function isInvalidOrder(value: Partial<Timeframe>): boolean {
  if (isFullTimeframe(value)) {
    const timezone = getTimezone();
    const fromTime = new TZDateMini(
      value.from?.absoluteDate,
      timezone,
    ).getTime();
    const toTime = new TZDateMini(value.to?.absoluteDate, timezone).getTime();
    if (value.from && value.to && fromTime >= toTime) {
      return true;
    }
  }
  return false;
}

function getInvalidErrorKey(
  value: { from: TimeValue | null; to: TimeValue | null } | TimeValue | null,
  valueMode?: 'single' | 'timeframeSelector' | 'dateTimePicker',
): TimeErrorKey | undefined {
  if (
    valueMode !== 'single' &&
    value &&
    !isTimeValue(value) &&
    (('from' in value && value.from !== undefined) ||
      ('to' in value && value.to !== undefined))
  ) {
    const isFromInvalid =
      value.from?.absoluteDate === '' && value.from?.value !== '';
    const isToInvalid = value.to?.absoluteDate === '' && value.to?.value !== '';

    if (isFromInvalid && isToInvalid) {
      return 'invalidFromTo';
    }
    if (isFromInvalid) {
      return 'invalidFrom';
    }
    if (isToInvalid) {
      return 'invalidTo';
    }
  }

  if (
    valueMode === 'single' &&
    value &&
    'absoluteDate' in value &&
    value.absoluteDate === '' &&
    value.value !== ''
  ) {
    return 'invalid';
  }

  return undefined;
}

/**
 * Determines the appropriate error key for a missing value.
 */
function getValueMissingErrorKey(
  value: { from: TimeValue | null; to: TimeValue | null } | TimeValue | null,
  valueMode?: 'single' | 'timeframeSelector' | 'dateTimePicker',
): TimeErrorKey | undefined {
  if (value === null) {
    if (valueMode === 'single') {
      return 'singleValueMissing';
    }

    if (valueMode === 'dateTimePicker') {
      return 'missingFromTo';
    }

    return 'valueMissing';
  }

  if (!isTimeValue(value) && value.from === null && value.to === null) {
    if (valueMode === 'dateTimePicker') {
      return 'missingFromTo';
    }

    return 'valueMissing';
  }

  if (valueMode !== 'single' && isPartialTimeframe(value)) {
    if (!value.from) {
      return 'missingFrom';
    }
    if (!value.to) {
      return 'missingTo';
    }
  }

  return undefined;
}

/**
 * Validates a Timeframe or a TimeValue against the provided constraints (min, max, required, order) and dispatches appropriate error messages.
 * @internal
 */
const VALIDITY_KEY_MAP: Partial<Record<TimeErrorKey, string>> = {
  minBoundary: 'rangeUnderflow',
  maxBoundary: 'rangeOverflow',
};

/**
 * Maps internal {@link TimeErrorKey} values to the external {@link ValidityStateKey}
 * exposed in `ValidationState`. Differs from `VALIDITY_KEY_MAP`, which drives
 * the internal `formMessageDispatch` key and must stay `'customError'` for
 * Strato-managed errors so that `setCustomValidity` / `onInvalid` fire correctly.
 */
const EXTERNAL_VALIDITY_KEY_MAP: Partial<Record<TimeErrorKey, string>> = {
  minBoundary: 'rangeUnderflow',
  maxBoundary: 'rangeOverflow',
  singleValueMissing: 'valueMissing',
  missingFromTo: 'valueMissing',
};

/**
 * Returns the {@link ValidityStateKey} for a {@link TimeErrorKey} for use in
 * `ValidationState` emitted to consumers. Applies native-key aliases so that
 * e.g. `singleValueMissing` surfaces as the native `valueMissing` key.
 * @internal
 */
export function getValidityStateKeyForTimeError(
  errorKey: TimeErrorKey,
): ValidityStateKey {
  return new ValidityStateKey(EXTERNAL_VALIDITY_KEY_MAP[errorKey] ?? errorKey);
}

export function validate(
  value: { from: TimeValue | null; to: TimeValue | null } | TimeValue | null,
  min?: TimeValue | null,
  max?: TimeValue | null,
  options?: ValidateOptions,
): TimeErrorKey | undefined {
  const {
    formMessageDispatch,
    formerError: previousError,
    precision: validationPrecision = 'minutes',
    showError: showValidationError,
    checkOrder: shouldCheckOrder,
    ref: validationRef,
    expectedFormat: expectedValidationFormat = 'YYYY-MM-DD HH:mm:ss',
    valueMode: validationValueMode,
    required: isRequired,
    errorMessages,
  } = options ?? {};

  const term = validationValueMode === 'single' ? 'date' : 'timeframe';

  const timeBoundaryMessageData = getTimeBoundaryMessageData(
    value,
    min,
    max,
    validationPrecision,
    undefined,
    term,
  );

  const invalidOrderError =
    shouldCheckOrder && isInvalidOrder(value as Partial<Timeframe>)
      ? 'invalidOrder'
      : undefined;
  const invalidError = getInvalidErrorKey(value, validationValueMode);
  const valueMissingError = isRequired
    ? getValueMissingErrorKey(value, validationValueMode)
    : undefined;
  const outOfBoundsError = timeBoundaryMessageData?.errorKey;
  const errorKey =
    invalidOrderError ?? valueMissingError ?? outOfBoundsError ?? invalidError;

  if (errorKey) {
    const messageDef =
      errorMessages?.[errorKey] ?? DEFAULT_ERROR_MESSAGES[errorKey];
    // Only add the error message if the erroring input is already mounted
    validationRef?.current &&
      formMessageDispatch?.({
        type: FormFieldMessagesAction.ADDMESSAGE,
        payload: {
          id: errorKey,
          variant: 'error',
          messageType: 'formcontrol',
          message: (
            <FormattedMessage
              {...messageDef}
              values={{
                ...timeBoundaryMessageData?.values,
                expectedFormat: expectedValidationFormat,
              }}
            />
          ),
          validityStateKey: VALIDITY_KEY_MAP[errorKey] ?? 'customError',
          showError: showValidationError,
        },
      });
  }

  // cleanup old error message
  if (previousError && previousError !== errorKey) {
    validationRef?.current &&
      formMessageDispatch?.({
        type: FormFieldMessagesAction.REMOVEMESSAGE,
        payload: {
          id: previousError,
          messageType: 'formcontrol',
        },
      });
  }

  return errorKey;
}

/**
 * Formats the human-readable message for a time validation error key.
 *
 * Handles interpolation for boundary errors (`{min}`, `{max}`) and format
 * errors (`{expectedFormat}`). Falls back to the error key string when no
 * message descriptor is found.
 *
 * @internal
 */
export function formatTimeValidationMessage(
  errorKey: TimeErrorKey,
  intl: IntlShape,
  options?: {
    value?: { from: TimeValue | null; to: TimeValue | null } | TimeValue | null;
    min?: TimeValue | null;
    max?: TimeValue | null;
    precision?: Precision;
    expectedFormat?: string;
    errorMessages?: Partial<Record<TimeErrorKey, MessageDescriptor>>;
  },
): string {
  const {
    value,
    min,
    max,
    precision = 'minutes',
    expectedFormat,
    errorMessages,
  } = options ?? {};

  const msgDef = errorMessages?.[errorKey] ?? DEFAULT_ERROR_MESSAGES[errorKey];

  if (!msgDef) {
    return errorKey;
  }

  if (isTimeBoundaryErrorKey(errorKey)) {
    return intl.formatMessage(
      msgDef,
      getTimeBoundaryMessageData(value ?? null, min, max, precision)?.values,
    );
  }

  return intl.formatMessage(
    msgDef,
    errorKey === 'invalid' ||
      errorKey === 'invalidFrom' ||
      errorKey === 'invalidTo' ||
      errorKey === 'invalidFromTo'
      ? { expectedFormat: expectedFormat ?? '' }
      : undefined,
  );
}
